import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Button } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useGetFailedTransactions, useRetryTransactions } from '@src/api/transaction-retry-api'
import { useSelectedCompany } from '@src/providers/SelectedCompanyProvider'
import { FailedTransactionItem } from '@src/types/transaction-retry'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const useTransactionRetrySelection = (transactions: FailedTransactionItem[] = []) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds])

  const isAllSelected = useMemo(() => {
    return transactions.length > 0 && transactions.every((transaction) => selectedIds.has(transaction.id))
  }, [transactions, selectedIds])

  const isIndeterminate = useMemo(() => {
    const selectedCount = transactions.filter((transaction) => selectedIds.has(transaction.id)).length
    return selectedCount > 0 && selectedCount < transactions.length
  }, [transactions, selectedIds])

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === transactions.length) {
        return new Set()
      } else {
        return new Set(transactions.map((t) => t.id))
      }
    })
  }, [transactions])

  const setSelectedIdsDirectly = useCallback((ids: Set<string> | string[]) => {
    setSelectedIds(ids instanceof Set ? ids : new Set(ids))
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const selectedTransactions = useMemo(() => {
    return transactions.filter((transaction) => selectedIds.has(transaction.id))
  }, [transactions, selectedIds])

  return {
    selectedIds,
    selectedTransactions,
    isSelected,
    isAllSelected,
    isIndeterminate,
    toggleSelection,
    toggleSelectAll,
    setSelectedIds: setSelectedIdsDirectly,
    clearSelection,
  }
}

export const useTransactionRetryActions = () => {
  const retryMutation = useRetryTransactions()

  const retryTransactions = useCallback(
    async (transactionIds: string[]) => {
      if (transactionIds.length === 0) {
        throw new Error('No transactions selected')
      }
      return retryMutation.mutateAsync(transactionIds)
    },
    [retryMutation],
  )

  return {
    retryTransactions,
    isRetrying: retryMutation.isPending,
    retryError: retryMutation.error,
    isRetrySuccess: retryMutation.isSuccess,
  }
}

export const useFailedTransactionsData = () => {
  const selectedCompany = useSelectedCompany()

  const [data, isLoading, error] = useGetFailedTransactions(selectedCompany ? String(selectedCompany) : undefined)

  const sortedTransactions = useMemo(() => {
    if (!data) return []
    return [...data].sort((a, b) => b.date.getTime() - a.date.getTime())
  }, [data])

  return {
    transactions: sortedTransactions,
    isLoading,
    error,
  }
}

interface TransactionRow {
  id: string // ID de la transaction
  fileName: string
  processDate: string
  errorCount: number
  transactionType: string
}

export const useTransactionDataGrid = (
  transactions: FailedTransactionItem[],
  onViewReport: (fileName: string) => void,
  onRetryProcessing: (fileName: string) => void,
) => {
  const { t } = useTranslation()

  // Fonction pour extraire le type de transaction du nom de fichier
  const extractTransactionType = useCallback(
    (fileName: string): string => {
      if (fileName.startsWith('journal-entries')) {
        // Format: journal-entries-LoanAdjustments-20251107.json
        const regex = /journal-entries-([^-]+)-/
        const match = regex.exec(fileName)
        if (match) {
          const suffix = match[1]
          return `${t('admin.transactionRetry.transactionTypes.journalEntries')} - ${suffix}`
        }
        return t('admin.transactionRetry.transactionTypes.journalEntries')
      } else if (fileName.startsWith('Provider-Payable')) {
        return t('admin.transactionRetry.transactionTypes.providerPayable')
      } else if (fileName.startsWith('Provider-Payment')) {
        return t('admin.transactionRetry.transactionTypes.providerPayment')
      }
      return t('admin.transactionRetry.transactionTypes.unknown')
    },
    [t],
  )

  // Fonction pour extraire la date du nom de fichier CSV
  const extractDateFromFileName = useCallback(
    (fileName: string): string => {
      if (fileName.startsWith('journal-entries')) {
        // Format: journal-entries-LoanAdjustments-20251107.csv
        const regex = /journal-entries-[^-]+-(\d{8})/
        const match = regex.exec(fileName)
        if (match) {
          const dateStr = match[1]
          const year = dateStr.substring(0, 4)
          const month = dateStr.substring(4, 6)
          const day = dateStr.substring(6, 8)
          return `${year}-${month}-${day}`
        }
      } else {
        // Format: Provider-Payable-20250829-20250915033230.csv
        //         Provider-Payment-20250829-20250915033230.csv
        const regex = /(?:Provider-Payable|Provider-Payment)-(\d{8})-/
        const match = regex.exec(fileName)
        if (match) {
          const dateStr = match[1]
          const year = dateStr.substring(0, 4)
          const month = dateStr.substring(4, 6)
          const day = dateStr.substring(6, 8)
          return `${year}-${month}-${day}`
        }
      }
      return t('admin.transactionRetry.labels.unknownDate')
    },
    [t],
  )

  // Fonction pour obtenir le nom du fichier JSON pour le retry
  const getJsonFileName = (csvFileName: string): string => {
    return csvFileName.replace('.csv', '.json')
  }

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'transactionType',
        headerName: t('admin.transactionRetry.labels.transactionType'),
        width: 160,
        flex: 1,
      },
      {
        field: 'processDate',
        headerName: t('admin.transactionRetry.labels.processDate'),
        width: 180,
        flex: 1,
      },
      {
        field: 'errorCount',
        headerName: t('admin.transactionRetry.labels.errorCount'),
        width: 150,
        type: 'number',
        valueFormatter: (value: number) => value?.toString() || '0',
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'actions',
        headerName: t('admin.transactionRetry.labels.actions'),
        width: 280,
        sortable: false,
        renderCell: (params: GridRenderCellParams<TransactionRow>) => (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={(e) => {
                e.stopPropagation()
                onViewReport(params.row.fileName)
              }}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              {t('admin.transactionRetry.labels.viewReport')}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={(e) => {
                e.stopPropagation()
                onRetryProcessing(getJsonFileName(params.row.fileName))
              }}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              {t('admin.transactionRetry.labels.relaunch')}
            </Button>
          </Box>
        ),
      },
    ]
  }, [onViewReport, onRetryProcessing, t])

  const rows = useMemo(() => {
    // Filtrer pour n'afficher que les fichiers CSV
    const csvTransactions = transactions.filter((transaction) => transaction.fileName.toLowerCase().endsWith('.csv'))

    const processedRows = csvTransactions.map((transaction) => ({
      id: transaction.id, // Utiliser directement l'ID original
      fileName: transaction.fileName,
      processDate: extractDateFromFileName(transaction.fileName),
      errorCount: transaction.errorCount || 0,
      transactionType: extractTransactionType(transaction.fileName),
    }))

    return processedRows
  }, [transactions, extractDateFromFileName, extractTransactionType])

  return {
    columns,
    rows,
  }
}
